// VIBRANIAM-SPACE v2 - GeometricMeshSubstrate.js
//
// A second, independent substrate: procedural 3D geometry (grids, polyhedra,
// wireframes) driven by the same analysis frame that feeds the particle field.
// It plugs into a layer exactly like ParticleFieldSubstrate does - it exposes a
// `points` Object3D and the same lifecycle methods - so the Engine treats the
// two interchangeably and a preset can use either, both, or neither.
//
// Nothing here touches ParticleFieldSubstrate, shaders.js or Pipeline.js.
//
// Deterministic by construction, like the rest of the engine: the only clock is
// an internal frame counter, per-vertex randomness is hashed from the vertex
// index, and the displacement is a pure function of (frame, params, audio).

import * as THREE from 'three';
import { MESH_VERT, MESH_FRAG, MESH_BANDS_MAX } from '../render/mesh-shaders.js';

/**
 * The geometry catalogue. `res` is the resolution parameter after clamping:
 * for subdivided polyhedra it is a detail level, elsewhere a segment count.
 * Keep the vertex counts modest - these are wireframes, and triangle count is
 * what costs, not the displacement (which is per-vertex on the GPU).
 */
export const MESH_KINDS = [
  {
    id: 0, label: 'Griglia', band: 'x',
    build: (res) => {
      const seg = Math.round(THREE.MathUtils.clamp(res, 4, 160));
      const g = new THREE.PlaneGeometry(3.4, 3.4, seg, seg);
      g.rotateX(-Math.PI / 2); // lie flat, normals point +Y
      return g;
    },
  },
  {
    id: 1, label: 'Sfera', band: 'azimuth',
    build: (res) => {
      const seg = Math.round(THREE.MathUtils.clamp(res, 6, 128));
      return new THREE.SphereGeometry(1.25, seg, Math.max(3, Math.round(seg / 2)));
    },
  },
  {
    id: 2, label: 'Icosaedro', band: 'azimuth',
    build: (res) => new THREE.IcosahedronGeometry(1.35, Math.round(THREE.MathUtils.clamp(res / 24, 0, 5))),
  },
  {
    id: 3, label: 'Toro', band: 'azimuth',
    build: (res) => {
      const seg = Math.round(THREE.MathUtils.clamp(res, 6, 160));
      return new THREE.TorusGeometry(1.15, 0.42, Math.max(4, Math.round(seg / 2)), seg);
    },
  },
  {
    id: 4, label: 'Nodo toroidale', band: 'azimuth',
    build: (res) => {
      const seg = Math.round(THREE.MathUtils.clamp(res * 2, 16, 400));
      return new THREE.TorusKnotGeometry(1.0, 0.3, seg, Math.max(4, Math.round(res / 4)));
    },
  },
  {
    id: 5, label: 'Cubo', band: 'azimuth',
    build: (res) => {
      const seg = Math.round(THREE.MathUtils.clamp(res / 2, 1, 60));
      return new THREE.BoxGeometry(1.9, 1.9, 1.9, seg, seg, seg);
    },
  },
  {
    id: 6, label: 'Ottaedro', band: 'azimuth',
    build: (res) => new THREE.OctahedronGeometry(1.4, Math.round(THREE.MathUtils.clamp(res / 24, 0, 5))),
  },
  {
    id: 7, label: 'Cilindro', band: 'azimuth',
    build: (res) => {
      const seg = Math.round(THREE.MathUtils.clamp(res, 4, 160));
      return new THREE.CylinderGeometry(1.0, 1.0, 2.2, seg, Math.max(1, Math.round(seg / 3)), true);
    },
  },
];

export const MESH_KIND_LABELS = MESH_KINDS.map((k) => k.label);

/**
 * Accepts either the label stored by the select control ('Sfera') or a numeric
 * index. Patches saved before a label was renamed still resolve by index, and a
 * value that matches nothing falls back to the first shape rather than throwing.
 */
function resolveKind(v) {
  if (typeof v === 'string') {
    const found = MESH_KINDS.find((k) => k.label === v);
    if (found) return found;
    const n = Number(v);
    if (Number.isFinite(n)) return MESH_KINDS[THREE.MathUtils.clamp(Math.round(n), 0, MESH_KINDS.length - 1)];
    return MESH_KINDS[0];
  }
  const n = Number(v);
  if (!Number.isFinite(n)) return MESH_KINDS[0];
  return MESH_KINDS[THREE.MathUtils.clamp(Math.round(n), 0, MESH_KINDS.length - 1)];
}

/** Deterministic per-vertex value in [0,1) - same vertex, same number, always. */
function hashIndex(i) {
  let h = Math.imul(i ^ 0x9e3779b9, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

export class GeometricMeshSubstrate {
  constructor(params = {}) {
    this.geometry = null;
    this._signature = '';
    this._frame = 0;
    this._onsetEnv = 0;

    this.material = new THREE.ShaderMaterial({
      vertexShader: MESH_VERT,
      fragmentShader: MESH_FRAG,
      uniforms: {
        uBands: { value: new Float32Array(MESH_BANDS_MAX) },
        uBandCount: { value: 32 },
        uTime: { value: 0 },
        uBass: { value: 0 },
        uRms: { value: 0 },
        uHigh: { value: 0 },
        uOnsetEnv: { value: 0 },
        uDisplace: { value: 0.6 },
        uWave: { value: 0.05 },
        uWaveSpeed: { value: 1.2 },
        uTwist: { value: 0.2 },
        uJitter: { value: 0.15 },
        uPulse: { value: 0.25 },
        uSpin: { value: 0.15 },
        uHueBase: { value: 0.55 },
        uHueSpread: { value: 0.25 },
        uHueByBand: { value: 0.3 },
        uSaturation: { value: 0.85 },
        uBrightness: { value: 0.9 },
        uOpacity: { value: 1 },
        uGain: { value: 0.8 },
      },
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      wireframe: true,
    });

    this.points = new THREE.Mesh(new THREE.BufferGeometry(), this.material);
    this.points.frustumCulled = false;

    this._rebuild(params.kind ?? MESH_KINDS[0].label, params.resolution ?? 48);
  }

  /**
   * Swaps in a new base geometry. Only called when the shape actually changes
   * (see the signature check in applyEmitter): rebuilding on every parameter
   * tick would churn GPU buffers for nothing.
   */
  _rebuild(kindValue, resolution) {
    const kind = resolveKind(kindValue);
    const geo = kind.build(resolution);
    geo.deleteAttribute('uv'); // never sampled; do not upload it

    const pos = geo.getAttribute('position');
    const n = pos.count;
    const band = new Float32Array(n);
    const seed = new Float32Array(n);

    // Which analysis band a vertex listens to. Azimuth wraps the spectrum
    // around the shape; the grid reads it left-to-right like a spectrogram.
    for (let i = 0; i < n; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);
      if (kind.band === 'x') {
        band[i] = THREE.MathUtils.clamp((x + 1.7) / 3.4, 0, 1);
      } else {
        band[i] = (Math.atan2(z, x) / (Math.PI * 2)) + 0.5;
      }
      seed[i] = hashIndex(i) * 100 + (y * 0.001);
    }

    geo.setAttribute('aBand', new THREE.BufferAttribute(band, 1));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    // The vertex shader pushes vertices well outside the base radius; skip
    // Three's culling maths rather than let it clip the shape mid-pulse.
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    // Same discipline as ParticleFieldSubstrate._allocate: the outgoing
    // geometry's GPU buffers are only freed when its 'dispose' event fires, so
    // dropping the reference alone would leak on every shape change (and the
    // resolution slider fires one of these per pointermove).
    const old = this.geometry;
    this.geometry = geo;
    this.points.geometry = geo;
    if (old) old.dispose();

    this._signature = `${kind.id}|${Math.round(resolution)}`;
    this.vertexCount = n;
    this.triangleCount = geo.index ? geo.index.count / 3 : n / 3;
  }

  /** Present for interface parity with ParticleFieldSubstrate. */
  setViewportHeight() {}

  setCapacity() {}

  applyEmitter(p) {
    const u = this.material.uniforms;
    const kind = resolveKind(p.kind);
    const res = Number(p.resolution) || 48;
    const wanted = `${kind.id}|${Math.round(res)}`;
    if (wanted !== this._signature) this._rebuild(p.kind, res);

    const num = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);
    u.uDisplace.value = num(p.displace, 0.6);
    u.uWave.value = num(p.wave, 0.05);
    u.uWaveSpeed.value = num(p.waveSpeed, 1.2);
    u.uTwist.value = num(p.twist, 0.2);
    u.uJitter.value = num(p.jitter, 0.15);
    u.uPulse.value = num(p.pulse, 0.25);
    u.uSpin.value = num(p.spin, 0.15);
    u.uHueBase.value = num(p.hueBase, 0.55);
    u.uHueSpread.value = num(p.hueSpread, 0.25);
    u.uHueByBand.value = num(p.hueByBand, 0.3);
    u.uSaturation.value = num(p.saturation, 0.85);
    u.uBrightness.value = num(p.brightness, 0.9);
    u.uOpacity.value = num(p.opacity, 1);
    u.uGain.value = num(p.gain, 0.8);

    const wire = p.wireframe !== false;
    if (this.material.wireframe !== wire) this.material.wireframe = wire;
  }

  setAudio(features) {
    const u = this.material.uniforms;
    const src = features?.bands;
    const dst = u.uBands.value;
    const n = src ? Math.min(src.length, MESH_BANDS_MAX) : 0;
    for (let i = 0; i < n; i++) dst[i] = src[i];
    for (let i = n; i < MESH_BANDS_MAX; i++) dst[i] = 0;
    u.uBandCount.value = Math.max(1, n);

    u.uBass.value = features?.bass || 0;
    u.uRms.value = features?.rms || 0;
    u.uHigh.value = features?.high || 0;
    this._onset = !!features?.onset;
  }

  step(dt, fps) {
    // Onset is an instant; give it a short decay so a hit reads as a flash
    // rather than a single-frame blink. Frame-rate independent.
    if (this._onset) this._onsetEnv = 1;
    else this._onsetEnv = Math.max(0, this._onsetEnv - dt * 4.5);
    this._onset = false;

    const u = this.material.uniforms;
    u.uOnsetEnv.value = this._onsetEnv;
    u.uTime.value = this._frame / Math.max(1, fps);
    this._frame++;
  }

  reset() {
    this._frame = 0;
    this._onsetEnv = 0;
    this._onset = false;
    this.material.uniforms.uTime.value = 0;
    this.material.uniforms.uOnsetEnv.value = 0;
  }

  /** Stats parity with the particle substrate: vertices stand in for particles. */
  get aliveCount() { return this.vertexCount || 0; }
  get capacity() { return this.vertexCount || 0; }
  get frameIndex() { return this._frame; }

  dispose() {
    // Detach first: the Engine drops the substrate from its map before the next
    // layer sync removes the object from the scene, and a render in that window
    // would otherwise touch freed buffers. The geometry reference is kept (just
    // disposed) rather than nulled, because a Mesh with geometry === null
    // throws inside Three's render loop.
    this.points.parent?.remove(this.points);
    this.geometry?.dispose();
    this.material.dispose();
  }
}
